import React, { useState, useEffect } from 'react';
import { planesAPI, suscripcionesAPI, pagosUsuariosAPI, usuariosAPI, emailAPI, authAPI, getSecureFileUrl } from '../services/api';
import './Suscripciones.css';

function Suscripciones() {
  const [tab, setTab] = useState('planes'); // planes, suscripciones, pagos
  const [currentUser, setCurrentUser] = useState(null);
  const [planes, setPlanes] = useState([]);
  const [suscripciones, setSuscripciones] = useState([]);
  const [pagos, setPagos] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Estados para formularios
  const [showPlanForm, setShowPlanForm] = useState(false);
  const [showSuscripcionForm, setShowSuscripcionForm] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [editingSuscripcion, setEditingSuscripcion] = useState(null);
  const [selectedUsuario, setSelectedUsuario] = useState(null);
  const [selectedSuscripcionId, setSelectedSuscripcionId] = useState(null);
  const [filtroFactura, setFiltroFactura] = useState('');

  // Email modal
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailPagoId, setEmailPagoId] = useState(null);
  const [formEmail, setFormEmail] = useState({ email: '', mensaje: '' });
  const [emailSending, setEmailSending] = useState(false);
  
  const [formPlan, setFormPlan] = useState({
    nombre: '',
    descripcion: '',
    precio_mensual: '',
    precio_anual: '',
    activo: true,
    caracteristicas: {
      clientes_max: -1,
      facturacion: true,
      oportunidades: true,
      tareas: true,
      interacciones: true
    }
  });
  
  const [formSuscripcion, setFormSuscripcion] = useState({
    usuario_id: '',
    plan_id: '',
    fecha_inicio: new Date().toISOString().split('T')[0],
    periodo: 'mensual',
    estado: 'activa'
  });
  
  useEffect(() => {
    authAPI.check().then(r => setCurrentUser(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, selectedUsuario, selectedSuscripcionId]);

  const loadData = async () => {
    try {
      setLoading(true);
      if (tab === 'planes') {
        const response = await planesAPI.getAll();
        setPlanes(response.data?.data || []);
      } else if (tab === 'suscripciones') {
        const [susRes, usrRes] = await Promise.all([
          suscripcionesAPI.getAll(),
          usuariosAPI.getAll(null)
        ]);
        setSuscripciones(susRes.data?.data || []);
        setUsuarios(usrRes.data || []);
      } else if (tab === 'pagos') {
        if (selectedUsuario) {
          const response = await pagosUsuariosAPI.getAll(null, selectedUsuario.id);
          let list = response.data?.data || [];
          if (selectedSuscripcionId) {
            list = list.filter(p => p.suscripcion_id === selectedSuscripcionId);
          }
          setPagos(list);
        } else {
          const response = await pagosUsuariosAPI.getAll();
          setPagos(response.data?.data || []);
        }
      }
      setError(null);
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  const handleEnviarEmailSuscripcion = async () => {
    if (!formEmail.email || !formEmail.email.includes('@')) {
      setError('Email válido requerido');
      return;
    }
    try {
      setEmailSending(true);
      setError(null);
      const response = await emailAPI.send('factura_suscripcion', emailPagoId, formEmail.email, formEmail.mensaje);
      if (response.data?.success) {
        setSuccess('Email enviado correctamente');
        setShowEmailModal(false);
        setFormEmail({ email: '', mensaje: '' });
        setTimeout(() => setSuccess(null), 4000);
      } else {
        setError(response.data?.error || 'Error al enviar email');
      }
    } catch (e) {
      setError(e.response?.data?.error || 'Error al enviar email');
    } finally {
      setEmailSending(false);
    }
  };

  const handleCreatePlan = async () => {
    try {
      if (editingPlan) {
        await planesAPI.update(editingPlan.id, formPlan);
        setSuccess('Plan actualizado correctamente');
      } else {
        await planesAPI.create(formPlan);
        setSuccess('Plan creado correctamente');
      }
      setShowPlanForm(false);
      setEditingPlan(null);
      setFormPlan({
        nombre: '',
        descripcion: '',
        precio_mensual: '',
        precio_anual: '',
        activo: true,
        caracteristicas: {
          clientes_max: -1,
          facturacion: true,
          oportunidades: true,
          tareas: true,
          interacciones: true
        }
      });
      loadData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || (editingPlan ? 'Error al actualizar el plan' : 'Error al crear el plan'));
    }
  };

  const handleCreateSuscripcion = async () => {
    try {
      const plan = planes.find(p => p.id === parseInt(formSuscripcion.plan_id));
      if (!plan) {
        setError('Plan no encontrado');
        return;
      }
      
      const fechaInicio = new Date(formSuscripcion.fecha_inicio);
      const fechaVencimiento = new Date(fechaInicio);
      
      if (formSuscripcion.periodo === 'mensual') {
        fechaVencimiento.setMonth(fechaVencimiento.getMonth() + 1);
      } else {
        fechaVencimiento.setFullYear(fechaVencimiento.getFullYear() + 1);
      }
      
      const payload = {
        ...formSuscripcion,
        fecha_vencimiento: fechaVencimiento.toISOString().split('T')[0],
      };

      if (editingSuscripcion) {
        // Edición: dejamos que el backend recalcule el precio si cambia plan/periodo
        await suscripcionesAPI.update(editingSuscripcion.id, payload);
        setSuccess('Suscripción actualizada correctamente');
      } else {
        // Creación: el backend calculará el precio a partir del plan
        await suscripcionesAPI.create(payload);
        setSuccess('Suscripción creada correctamente');
      }
      
      setShowSuscripcionForm(false);
      setEditingSuscripcion(null);
      setFormSuscripcion({
        usuario_id: '',
        plan_id: '',
        fecha_inicio: new Date().toISOString().split('T')[0],
        periodo: 'mensual',
        estado: 'activa'
      });
      loadData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || (editingSuscripcion ? 'Error al actualizar la suscripción' : 'Error al crear la suscripción'));
    }
  };

  const handleMarcarPago = async (pagoId, data = {}) => {
    try {
      await pagosUsuariosAPI.marcarPagado(pagoId, data);
      setSuccess('Pago marcado como pagado');
      loadData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al marcar el pago');
    }
  };

  const handleGenerarPagos = async (suscripcionId) => {
    try {
      await pagosUsuariosAPI.generarPagos(suscripcionId, 12);
      setSuccess('Pagos generados correctamente');
      loadData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al generar los pagos');
    }
  };

  const handleVerSuscripcion = (suscripcion) => {
    setSelectedUsuario({ id: suscripcion.usuario_id, nombre: suscripcion.usuario_nombre, apellidos: suscripcion.usuario_apellidos });
    setSelectedSuscripcionId(suscripcion.id);
    setTab('pagos');
  };

  if (loading && tab === 'planes' && planes.length === 0) {
    return <div className="loading">Cargando...</div>;
  }

  return (
    <div className="suscripciones">
      <div className="page-header">
        <h2>Gestión de Suscripciones y Pagos</h2>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="tabs">
        <button 
          className={`tab ${tab === 'planes' ? 'active' : ''}`}
          onClick={() => setTab('planes')}
        >
          Planes
        </button>
        <button 
          className={`tab ${tab === 'suscripciones' ? 'active' : ''}`}
          onClick={() => setTab('suscripciones')}
        >
          Suscripciones
        </button>
        <button 
          className={`tab ${tab === 'pagos' ? 'active' : ''}`}
          onClick={() => setTab('pagos')}
        >
          Pagos
        </button>
      </div>

      {/* Tab Planes */}
      {tab === 'planes' && (
        <div className="tab-content">
          <div style={{ marginBottom: '20px' }}>
            <button
              className="btn btn-primary"
              onClick={() => {
                setEditingPlan(null);
                setFormPlan({
                  nombre: '',
                  descripcion: '',
                  precio_mensual: '',
                  precio_anual: '',
                  activo: true,
                  caracteristicas: {
                    clientes_max: -1,
                    facturacion: true,
                    oportunidades: true,
                    tareas: true,
                    interacciones: true
                  }
                });
                setShowPlanForm(true);
              }}
            >
              + Nuevo Plan
            </button>
          </div>

          {showPlanForm && (
            <div
              className="suscripcion-modal-overlay"
              onClick={() => {
                setShowPlanForm(false);
                setEditingPlan(null);
              }}
            >
              <div className="suscripcion-modal-box" onClick={(e) => e.stopPropagation()}>
                <h3>{editingPlan ? 'Editar' : 'Nuevo'} Plan</h3>
                <div className="form-group">
                  <label>Nombre *</label>
                  <input 
                    type="text" 
                    value={formPlan.nombre}
                    onChange={(e) => setFormPlan({ ...formPlan, nombre: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Descripción</label>
                  <textarea 
                    value={formPlan.descripcion}
                    onChange={(e) => setFormPlan({ ...formPlan, descripcion: e.target.value })}
                    rows={3}
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Precio Mensual (€) *</label>
                    <input 
                      type="number" 
                      step="0.01"
                      value={formPlan.precio_mensual}
                      onChange={(e) => setFormPlan({ ...formPlan, precio_mensual: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Precio Anual (€)</label>
                    <input 
                      type="number" 
                      step="0.01"
                      value={formPlan.precio_anual}
                      onChange={(e) => setFormPlan({ ...formPlan, precio_anual: e.target.value })}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>
                    <input 
                      type="checkbox"
                      checked={formPlan.activo}
                      onChange={(e) => setFormPlan({ ...formPlan, activo: e.target.checked })}
                    />
                    Activo
                  </label>
                </div>
                <div className="modal-actions">
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowPlanForm(false);
                      setEditingPlan(null);
                    }}
                  >
                    Cancelar
                  </button>
                  <button className="btn btn-primary" onClick={handleCreatePlan}>
                    Guardar
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Precio Mensual</th>
                  <th>Precio Anual</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {planes.map(plan => (
                  <tr key={plan.id}>
                    <td><strong>{plan.nombre}</strong></td>
                    <td>{Number(plan.precio_mensual).toFixed(2)} €</td>
                    <td>{plan.precio_anual ? Number(plan.precio_anual).toFixed(2) + ' €' : '-'}</td>
                    <td>
                      <span className={`badge ${plan.activo ? 'badge-success' : 'badge-secondary'}`}>
                        {plan.activo ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td>
                      <button 
                        className="btn btn-edit btn-sm"
                        onClick={() => {
                          setEditingPlan(plan);
                          setFormPlan({
                            nombre: plan.nombre,
                            descripcion: plan.descripcion || '',
                            precio_mensual: plan.precio_mensual,
                            precio_anual: plan.precio_anual || '',
                            activo: plan.activo,
                            caracteristicas: plan.caracteristicas || {}
                          });
                          setShowPlanForm(true);
                        }}
                      >
                        Editar
                      </button>
                      {' '}
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={async () => {
                          if (!window.confirm('ATENCIÓN: Esto eliminará definitivamente el plan y no se podrá recuperar. ¿Seguro que quieres continuar?')) {
                            return;
                          }
                          try {
                            await planesAPI.delete(plan.id, true);
                            setSuccess('Plan eliminado definitivamente');
                            loadData();
                            setTimeout(() => setSuccess(null), 3000);
                          } catch (err) {
                            setError(err.response?.data?.error || 'Error al eliminar el plan. Comprueba que no tenga suscripciones asociadas.');
                          }
                        }}
                      >
                        Eliminar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tab Suscripciones */}
      {tab === 'suscripciones' && (
        <div className="tab-content">
          <div style={{ marginBottom: '20px' }}>
            <button className="btn btn-primary" onClick={() => {
              setEditingSuscripcion(null);
              setFormSuscripcion({
                usuario_id: '',
                plan_id: '',
                fecha_inicio: new Date().toISOString().split('T')[0],
                periodo: 'mensual',
                estado: 'activa'
              });
              setShowSuscripcionForm(true);
            }}>
              + Nueva Suscripción
            </button>
          </div>

          {showSuscripcionForm && (
            <div className="suscripcion-modal-overlay" onClick={() => {
              setShowSuscripcionForm(false);
              setEditingSuscripcion(null);
            }}>
              <div className="suscripcion-modal-box" onClick={(e) => e.stopPropagation()}>
                <h3>{editingSuscripcion ? 'Editar Suscripción' : 'Nueva Suscripción'}</h3>
                <div className="form-group">
                  <label>Usuario *</label>
                  <select 
                    value={formSuscripcion.usuario_id}
                    onChange={(e) => setFormSuscripcion({ ...formSuscripcion, usuario_id: e.target.value })}
                  >
                    <option value="">Seleccionar usuario...</option>
                    {usuarios.filter(u => u.rol !== 'admin').map(u => (
                      <option key={u.id} value={u.id}>
                        {u.nombre} {u.apellidos} ({u.username})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Plan *</label>
                  <select 
                    value={formSuscripcion.plan_id}
                    onChange={(e) => setFormSuscripcion({ ...formSuscripcion, plan_id: e.target.value })}
                  >
                    <option value="">Seleccionar plan...</option>
                    {planes.filter(p => p.activo).map(p => (
                      <option key={p.id} value={p.id}>
                        {p.nombre} - {Number(p.precio_mensual).toFixed(2)}€/mes
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Fecha Inicio *</label>
                    <input 
                      type="date" 
                      value={formSuscripcion.fecha_inicio}
                      onChange={(e) => setFormSuscripcion({ ...formSuscripcion, fecha_inicio: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Periodo *</label>
                    <select 
                      value={formSuscripcion.periodo}
                      onChange={(e) => setFormSuscripcion({ ...formSuscripcion, periodo: e.target.value })}
                    >
                      <option value="mensual">Mensual</option>
                      <option value="anual">Anual</option>
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label>Estado</label>
                  <select
                    value={formSuscripcion.estado}
                    onChange={(e) => setFormSuscripcion({ ...formSuscripcion, estado: e.target.value })}
                  >
                    <option value="activa">Activa</option>
                    <option value="vencida">Vencida</option>
                    <option value="suspendida">Suspendida</option>
                    <option value="cancelada">Cancelada</option>
                  </select>
                </div>
                <div className="modal-actions">
                  <button className="btn btn-secondary" onClick={() => {
                    setShowSuscripcionForm(false);
                    setEditingSuscripcion(null);
                  }}>
                    Cancelar
                  </button>
                  <button className="btn btn-primary" onClick={handleCreateSuscripcion}>
                    {editingSuscripcion ? 'Guardar Cambios' : 'Crear Suscripción'}
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Usuario</th>
                  <th>Plan</th>
                  <th>Fecha Inicio</th>
                  <th>Fecha Vencimiento</th>
                  <th>Periodo</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {suscripciones.map(sus => (
                  <tr key={sus.id}>
                    <td>{sus.usuario_nombre} {sus.usuario_apellidos}</td>
                    <td>{sus.plan_nombre}</td>
                    <td>{new Date(sus.fecha_inicio).toLocaleDateString('es-ES')}</td>
                    <td>{new Date(sus.fecha_vencimiento).toLocaleDateString('es-ES')}</td>
                    <td>{sus.periodo}</td>
                    <td>
                      <span className={`badge ${
                        sus.estado === 'activa' ? 'badge-success' :
                        sus.estado === 'vencida' ? 'badge-danger' :
                        'badge-secondary'
                      }`}>
                        {sus.estado}
                      </span>
                    </td>
                    <td>
                      <button 
                        className="btn btn-edit btn-sm"
                        onClick={() => {
                          setEditingSuscripcion(sus);
                          setFormSuscripcion({
                            usuario_id: sus.usuario_id,
                            plan_id: sus.plan_id,
                            fecha_inicio: sus.fecha_inicio,
                            periodo: sus.periodo,
                            estado: sus.estado
                          });
                          setShowSuscripcionForm(true);
                        }}
                      >
                        Editar
                      </button>
                      {' '}
                      <button 
                        className="btn btn-secondary btn-sm"
                        onClick={() => handleVerSuscripcion(sus)}
                      >
                        Ver Pagos
                      </button>
                      <button 
                        className="btn btn-primary btn-sm"
                        style={{ marginLeft: '5px' }}
                        onClick={() => handleGenerarPagos(sus.id)}
                      >
                        Generar Pagos
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tab Pagos */}
      {tab === 'pagos' && (
        <div className="tab-content">
          {selectedUsuario && (
            <div style={{ marginBottom: '15px', padding: '10px', background: '#f0f0f0', borderRadius: '5px' }}>
              <strong>Pagos de:</strong> {selectedUsuario.nombre} {selectedUsuario.apellidos}
              <button 
                className="btn btn-secondary btn-sm"
                style={{ marginLeft: '10px' }}
                onClick={() => {
                  setSelectedUsuario(null);
                  setSelectedSuscripcionId(null);
                }}
              >
                Ver Todos
              </button>
            </div>
          )}

          <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
            <label style={{ fontSize: 13, fontWeight: 600 }}>Filtrar por factura:</label>
            <select
              value={filtroFactura}
              onChange={e => setFiltroFactura(e.target.value)}
              style={{ padding: '5px 10px', borderRadius: 4, border: '1px solid #ccc', fontSize: 13 }}
            >
              <option value="">Todos</option>
              <option value="con">Con factura</option>
              <option value="sin">Sin factura</option>
            </select>
            {filtroFactura && (
              <span style={{ fontSize: 12, color: '#888' }}>
                {pagos.filter(p => filtroFactura === 'con' ? p.factura_archivo : !p.factura_archivo).length} de {pagos.length} pagos
              </span>
            )}
          </div>

          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Usuario</th>
                  <th>Plan</th>
                  <th>Fecha Vencimiento</th>
                  <th>Importe</th>
                  <th>Estado</th>
                  <th>Método Pago</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {pagos.filter(p => {
                  if (filtroFactura === 'con') return !!p.factura_archivo;
                  if (filtroFactura === 'sin') return !p.factura_archivo;
                  return true;
                }).map(pago => (
                  <tr key={pago.id} className={pago.estado === 'vencido' ? 'row-vencido' : ''}>
                    <td>{pago.usuario_nombre} {pago.usuario_apellidos}</td>
                    <td>{pago.plan_nombre}</td>
                    <td>{new Date(pago.fecha_vencimiento).toLocaleDateString('es-ES')}</td>
                    <td>{Number(pago.importe).toFixed(2)} €</td>
                    <td>
                      <span className={`badge ${
                        pago.estado === 'pagado' ? 'badge-success' :
                        pago.estado === 'vencido' ? 'badge-danger' :
                        'badge-warning'
                      }`}>
                        {pago.estado}
                      </span>
                    </td>
                    <td>{pago.metodo_pago || '-'}</td>
                    <td>
                      {pago.estado === 'pendiente' && (
                        <button 
                          className="btn btn-success btn-sm"
                          onClick={() => {
                            const metodo = prompt('Método de pago (transferencia, tarjeta, paypal, etc):', 'transferencia');
                            const referencia = prompt('Referencia o número de transacción (opcional):', '');
                            if (metodo) {
                              handleMarcarPago(pago.id, { metodo_pago: metodo, referencia });
                            }
                          }}
                        >
                          Marcar Pagado
                        </button>
                      )}
                      {' '}
                      {!pago.factura_archivo && (
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={async () => {
                            const numero = prompt('Número de factura para este pago:', '');
                            if (!numero) return;
                            try {
                              await pagosUsuariosAPI.generarFactura(pago.id, numero);
                              // Recargar todos los pagos desde el backend para reflejar la nueva factura
                              await loadData();
                              setSuccess('Factura generada correctamente');
                              setTimeout(() => setSuccess(null), 3000);
                            } catch (err) {
                              setError(err.response?.data?.error || 'Error al generar la factura');
                            }
                          }}
                        >
                          Crear factura
                        </button>
                      )}
                      {pago.factura_archivo && (
                        <>
                          <button
                            className="btn btn-secondary btn-sm"
                            style={{ marginLeft: '5px' }}
                            onClick={() => {
                              window.open(getSecureFileUrl(pago.factura_archivo), '_blank', 'noopener');
                            }}
                          >
                            Ver factura
                          </button>
                          <button
                            className="btn btn-secondary btn-sm"
                            style={{ marginLeft: '5px' }}
                            onClick={async () => {
                              const nuevoNum = prompt('Nuevo número de factura:', pago.factura_numero || '');
                              if (!nuevoNum || nuevoNum === pago.factura_numero) return;
                              try {
                                await pagosUsuariosAPI.editarNumeroFactura(pago.id, nuevoNum);
                                await loadData();
                                setSuccess('Número de factura actualizado');
                                setTimeout(() => setSuccess(null), 3000);
                              } catch (err) {
                                setError(err.response?.data?.error || 'Error al actualizar el número');
                              }
                            }}
                          >
                            Editar nº
                          </button>
                          <button
                            className="btn btn-info btn-sm"
                            style={{ marginLeft: '5px' }}
                            onClick={() => {
                              setEmailPagoId(pago.id);
                              setFormEmail({ email: pago.usuario_email || '', mensaje: '' });
                              setShowEmailModal(true);
                              setError(null);
                            }}
                          >
                            Enviar email
                          </button>
                          {(currentUser?.rol === 'admin' || currentUser?.whatsapp_habilitado === true || currentUser?.whatsapp_habilitado === 1) && (
                            <button
                              className="btn btn-sm"
                              style={{ marginLeft: '5px', background: '#25D366', color: '#fff', border: 'none' }}
                              onClick={() => {
                                const phone = pago.usuario_telefono || '';
                                if (!phone) { alert('Este usuario no tiene teléfono registrado'); return; }
                                const cleanPhone = phone.replace(/[^0-9]/g, '');
                                const nombre = `${pago.usuario_nombre || ''} ${pago.usuario_apellidos || ''}`.trim();
                                let texto = `Hola ${nombre}, te envío tu factura de suscripción (${pago.plan_nombre || 'plan'}) nº ${pago.factura_numero || ''}.`;
                                if (pago.factura_archivo) {
                                  texto += `\n\nPuedes verla aquí: ${window.location.origin}${getSecureFileUrl(pago.factura_archivo)}`;
                                }
                                window.open(`https://wa.me/${cleanPhone}?text=${encodeURIComponent(texto)}`, '_blank');
                              }}
                              title="Enviar por WhatsApp"
                            >
                              WhatsApp
                            </button>
                          )}
                          <button
                            className="btn btn-danger btn-sm"
                            style={{ marginLeft: '5px' }}
                            onClick={async () => {
                              if (!window.confirm('¿Eliminar la factura asociada a este pago?')) return;
                              try {
                                await pagosUsuariosAPI.eliminarFactura(pago.id);
                                await loadData();
                                setSuccess('Factura eliminada correctamente');
                                setTimeout(() => setSuccess(null), 3000);
                              } catch (err) {
                                setError(err.response?.data?.error || 'Error al eliminar la factura');
                              }
                            }}
                          >
                            Borrar factura
                          </button>
                        </>
                      )}
                      {' '}
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={async () => {
                          if (!window.confirm('ATENCIÓN: Esto eliminará definitivamente el pago y no se podrá recuperar. ¿Seguro que quieres continuar?')) {
                            return;
                          }
                          try {
                            await pagosUsuariosAPI.delete(pago.id);
                            setSuccess('Pago eliminado correctamente');
                            loadData();
                            setTimeout(() => setSuccess(null), 3000);
                          } catch (err) {
                            setError(err.response?.data?.error || 'Error al eliminar el pago');
                          }
                        }}
                      >
                        Eliminar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pagos.length === 0 && (
            <p className="empty-state">No hay pagos registrados</p>
          )}
        </div>
      )}

      {showEmailModal && (
        <div className="modal-overlay" onClick={() => setShowEmailModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <h3>Enviar Factura de Suscripción por Email</h3>
            <div className="form-group">
              <label>Email del destinatario *</label>
              <input
                type="email"
                value={formEmail.email}
                onChange={(e) => setFormEmail(f => ({ ...f, email: e.target.value }))}
                placeholder="usuario@email.com"
                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
              />
            </div>
            <div className="form-group">
              <label>Mensaje personalizado (opcional)</label>
              <textarea
                value={formEmail.mensaje}
                onChange={(e) => setFormEmail(f => ({ ...f, mensaje: e.target.value }))}
                rows={4}
                placeholder="Añade un mensaje personalizado que aparecerá antes del contenido estándar..."
                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
              />
            </div>
            <div className="form-actions" style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button type="button" className="btn btn-secondary" onClick={() => {
                setShowEmailModal(false);
                setFormEmail({ email: '', mensaje: '' });
              }}>
                Cancelar
              </button>
              <button type="button" className="btn btn-primary" onClick={handleEnviarEmailSuscripcion} disabled={emailSending}>
                {emailSending ? 'Enviando...' : 'Enviar Email'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Suscripciones;
